// Utils and Constants
import {
  INQUIRY_TYPE_TO_REQUEST_TYPE,
  ORIGINAL_INQUIRY,
  ELIGIBILITY_SEARCH_QUERIES_ALL,
  COLUMNS_CPT,
  COLUMNS_MAXIMUMS,
} from "utils/constants";
import { ParseTime } from "utils/parseTime";
import parseEligibilitySearch from "./LegacyTransforms";

interface PrintData {
  requestDetails: any;
  patientName: string;
  subject: string;
  topic: string;
  status: string;
  time: string[];
  requestID: string;
  tableResults?: any;
  eligibilitySearchJson?: any;
}

// Main print function
export const printRequestDetails = (printData: PrintData) => {
  // Create a new window for printing
  const printWindow = window.open("", "_blank", "width=800,height=600");

  if (!printWindow) {
    alert("Please allow popups to print the request details.");
    return;
  }

  // Helper function to get eligibility search question labels
  const getEligibilityQuestionLabels = () => {
    if (!printData.requestDetails?.eligibility_search_queries?.length)
      return "";

    const labels: string[] = [];
    printData.requestDetails.eligibility_search_queries.forEach(
      (searchQuery: string) => {
        const found = ELIGIBILITY_SEARCH_QUERIES_ALL.find(
          (item) => item.value === searchQuery
        );
        if (found) {
          labels.push(found.label);
        }
      }
    );
    return labels.join(", ");
  };

  // Create the HTML structure
  const printHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Request Details - ${printData.requestID}</title>
      <meta charset="utf-8">
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.4; color: #333; }
        .print-container { padding: 20px; max-width: 800px; margin: 0 auto; }
        .print-header { border-bottom: 2px solid #000; padding-bottom: 20px; margin-bottom: 20px; }
        .print-section { margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; }
        .print-section-title { font-size: 16px; font-weight: bold; margin-bottom: 10px; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
        .print-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
        .print-field { margin-bottom: 10px; }
        .print-field-label { font-weight: bold; font-size: 12px; color: #666; }
        .print-field-value { font-size: 14px; color: #333; margin-top: 2px; }
        .print-status { padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; }
        .print-status-success { background-color: #d4edda; color: #155724; }
        .print-status-pending { background-color: #fff3cd; color: #856404; }
        .print-status-failed { background-color: #f8d7da; color: #721c24; }
        .print-no-data { text-align: center; color: #666; font-style: italic; padding: 20px; }
        .header-flex { display: flex; align-items: center; justify-content: space-between; margin-bottom: 15px; }
        .header-content { display: flex; align-items: center; gap: 15px; }
        .icon-container { width: 40px; height: 40px; background-color: #f5f5f5; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
        .header-title { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
        .header-subtitle { color: #666; }
        .request-type { font-size: 20px; font-weight: 600; }
        .request-subject { color: #666; margin-top: 5px; }
        .footer { text-align: center; font-size: 12px; color: #666; margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; }
        .table-section { margin-bottom: 20px; }
        .table-title { font-size: 14px; font-weight: bold; margin-bottom: 10px; color: #333; }
        .print-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
        .print-table th, .print-table td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; font-size: 12px; }
        .print-table th { background-color: #f5f5f5; font-weight: bold; }
        .json-section { margin-bottom: 20px; }
        .json-title { font-size: 14px; font-weight: bold; margin-bottom: 10px; color: #333; }
        .json-content { background-color: #f8f9fa; padding: 15px; border-radius: 4px; font-family: 'Courier New', monospace; font-size: 10px; white-space: pre-wrap; word-break: break-word; max-height: 400px; overflow-y: auto; }

        @media print {
          body { margin: 0; padding: 0; }
          .print-container { margin: 0; padding: 15px; }
          .print-section { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="print-header">
          <div class="header-flex">
            <div class="header-content">
              <div class="icon-container">📄</div>
              <div>
                <div class="header-title">Request Details</div>
                <div class="header-subtitle">Request ID: ${
                  printData.requestID
                }</div>
              </div>
            </div>
            <div class="print-status print-status-${printData.status.toLowerCase()}">
              ${printData.status}
            </div>
          </div>

          <div>
            <div class="request-type">
              ${
                INQUIRY_TYPE_TO_REQUEST_TYPE[printData.topic] || printData.topic
              }
            </div>
            <div class="request-subject">${printData.subject}</div>
          </div>
        </div>

        <div class="print-section">
          <div class="print-section-title">Basic Information</div>
          <div class="print-grid">
            <div class="print-field">
              <div class="print-field-label">Patient Name</div>
              <div class="print-field-value">${printData.patientName}</div>
            </div>

            <div class="print-field">
              <div class="print-field-label">Request Created</div>
              <div class="print-field-value">${printData.time.join(", ")}</div>
            </div>

            ${
              printData.requestDetails?.claims_date_of_service
                ? `
              <div class="print-field">
                <div class="print-field-label">Date of Service</div>
                <div class="print-field-value">${ParseTime(
                  printData.requestDetails.claims_date_of_service
                ).join(", ")}</div>
              </div>
            `
                : ""
            }

            ${
              getEligibilityQuestionLabels()
                ? `
              <div class="print-field" style="grid-column: 1 / -1;">
                <div class="print-field-label">Search Queries</div>
                <div class="print-field-value">${getEligibilityQuestionLabels()}</div>
              </div>
            `
                : ""
            }
          </div>
        </div>

        <div class="print-section">
          <div class="print-section-title">Additional Request Details</div>
          ${getAdditionalDetailsHtml(printData.requestDetails)}
        </div>

        ${getTableResultsHtml(printData.tableResults)}

        ${getEligibilityJsonHtml(printData.eligibilitySearchJson)}

        <div class="footer">
          <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          <p>Health Harbor - Request Management System</p>
        </div>
      </div>
    </body>
    </html>
  `;

  // Write the HTML to the new window
  printWindow.document.write(printHtml);
  printWindow.document.close();

  // Wait for the content to load, then print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };
};

// Helper function to generate additional details HTML
function getAdditionalDetailsHtml(requestDetails: any): string {
  if (!requestDetails || !ORIGINAL_INQUIRY?.length) {
    return '<div class="print-no-data">No additional request details available</div>';
  }

  const validFields = ORIGINAL_INQUIRY.filter((item) => {
    const fieldValue = requestDetails[item.key];
    if (typeof fieldValue === "boolean") {
      return true;
    }
    return fieldValue != null && fieldValue !== "";
  });

  if (validFields.length === 0) {
    return '<div class="print-no-data">No additional request details available</div>';
  }

  const fieldsHtml = validFields
    .map((item) => {
      const fieldValue = requestDetails[item.key];
      let displayValue = fieldValue;

      if (typeof fieldValue === "boolean") {
        displayValue = fieldValue ? "✓ Yes" : "✗ No";
      }

      return `
      <div class="print-field">
        <div class="print-field-label">${item.value}</div>
        <div class="print-field-value">${displayValue}</div>
      </div>
    `;
    })
    .join("");

  return `<div class="print-grid">${fieldsHtml}</div>`;
}

// Helper function to generate table results HTML
function getTableResultsHtml(tableResults: any): string {
  if (
    !tableResults ||
    !tableResults.sections ||
    tableResults.sections.length === 0
  ) {
    return "";
  }

  const sectionsHtml = tableResults.sections
    .map((section: any) => {
      if (!section.rows || section.rows.length === 0) {
        return "";
      }

      const headers = section.columns
        .map((col: any) => `<th>${col.Header || col.accessor}</th>`)
        .join("");

      const rows = section.rows
        .map((row: any) => {
          const cells = section.columns
            .map((col: any) => {
              const value = row[col.accessor] || "";
              return `<td>${value}</td>`;
            })
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");

      return `
        <div class="table-section">
          <div class="table-title">${section.title}</div>
          <table class="print-table">
            <thead>
              <tr>${headers}</tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
        </div>
      `;
    })
    .join("");

  return `
    <div class="print-section">
      <div class="print-section-title">Search Results</div>
      ${sectionsHtml}
    </div>
  `;
}

// Helper function to generate eligibility JSON HTML (as tables like RequestFormParser)
function getEligibilityJsonHtml(eligibilitySearchJson: any): string {
  if (!eligibilitySearchJson) {
    return "";
  }

  try {
    // Use the same parsing logic as RequestFormParser
    const parsedJson = parseEligibilitySearch(eligibilitySearchJson);
    const tablesHtml = formatParsedJsonToTables(parsedJson);

    if (!tablesHtml) {
      return "";
    }

    return `
      <div class="print-section">
        <div class="print-section-title">Detailed Results</div>
        ${tablesHtml}
      </div>
    `;
  } catch (error) {
    console.error("Error parsing eligibility JSON:", error);
    // Fallback to raw JSON if parsing fails
    const jsonString = JSON.stringify(eligibilitySearchJson, null, 2);
    return `
      <div class="print-section">
        <div class="print-section-title">Detailed Results</div>
        <div class="json-section">
          <div class="json-title">Raw Data (Parsing Failed)</div>
          <div class="json-content">${jsonString}</div>
        </div>
      </div>
    `;
  }
}

// Helper function to convert parsed JSON to HTML tables (replicates RequestFormParser logic)
function formatParsedJsonToTables(parsedJson: any[]): string {
  const tablesHtml = parsedJson
    .map((item) => {
      //Check if there are groups
      if (item[0]["groups"] !== undefined) {
        if (item[0].groups[0][0].data !== undefined) {
          // if there is just one level of group, gather all the data under one header and set of columns
          const groupData: any[] = [];
          item[0].groups.forEach((group: any) => {
            groupData.push(group[0].data);
          });
          return createTableHtml(
            item[0].title.split("_").join(" "),
            groupData,
            item[0].groups[0][0].columns
          );
        } else {
          // if there are multiple levels of groups, map the data for each group into a table
          return item[0].groups
            .map((group: any) => {
              const originalCols = group[1][1][0].columns;
              const groupColumns: any[] = [];
              originalCols.forEach((key: any) => {
                // Don't exclude any columns - include all of them
                let temp = key;
                COLUMNS_CPT.forEach((val: any) => {
                  if (key.accessor === val.accessor) {
                    temp = val;
                  }
                });
                groupColumns.push(temp);
              });
              return createTableHtml(
                group[1][0][0].procedure_code_group_name,
                group[1][1][0].data,
                groupColumns
              );
            })
            .join("");
        }
      } else {
        // If there are no groups, check if we need to add $ signs
        const originalCols = item[0].columns;
        const groupColumns: any[] = [];
        originalCols.forEach((key: any) => {
          let temp = key;
          COLUMNS_MAXIMUMS.forEach((val: any) => {
            if (key.accessor === val.accessor) {
              temp = val;
            }
          });
          groupColumns.push(temp);
        });
        return createTableHtml(item[0].title, [item[0].data], groupColumns);
      }
    })
    .join("");

  return tablesHtml;
}

// Helper function to create HTML table from data
function createTableHtml(title: string, data: any[], columns: any[]): string {
  if (!data || data.length === 0) {
    return "";
  }

  const headers = columns
    .map((col: any) => `<th>${col.Header || col.accessor}</th>`)
    .join("");

  const rows = data
    .map((row: any) => {
      const cells = columns
        .map((col: any) => {
          let value = row[col.accessor] || "";

          // Apply cell formatting if available
          if (col.Cell && typeof col.Cell === "function") {
            try {
              // Try to apply the cell formatter, but handle errors gracefully
              const cellResult = col.Cell({ value });
              // If it's a React element, extract text content or use original value
              value =
                cellResult && typeof cellResult === "object"
                  ? value
                  : cellResult || value;
            } catch (error) {
              // If formatting fails, use original value
              console.warn("Cell formatting failed for", col.accessor, error);
            }
          }

          return `<td>${value}</td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  return `
    <div class="table-section">
      <div class="table-title">${title}</div>
      <table class="print-table">
        <thead>
          <tr>${headers}</tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>
    </div>
  `;
}
